import { useState } from 'react';
import { Phone, MessageCircle, FileText, Clock, User, Tag, MapPin, Activity, ChevronDown } from 'lucide-react';
import { Drawer } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { StatusBadge, SourceBadge } from '@/components/ui/Badge';
import { useLeadDetail, useAddRemark, useUpdateLead } from '@/hooks/useLeads';
import { formatDate, formatDateTime, formatPhone } from '@/utils/formatters';
import { Spinner } from '@/components/ui/Spinner';
import type { Lead, LeadStatus } from '@/types';
import { clsx } from 'clsx';

interface LeadDetailDrawerProps {
  lead: Lead | null;
  onClose: () => void;
}

const STATUSES: { value: LeadStatus; label: string }[] = [
  { value: 'new', label: 'New Lead' },
  { value: 'followup', label: 'Follow Up' },
  { value: 'opd_scheduled', label: 'OPD Scheduled' },
  { value: 'opd_done', label: 'OPD Done' },
  { value: 'ipd_scheduled', label: 'IPD Scheduled' },
  { value: 'ipd_done', label: 'IPD Done' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'preauth', label: 'Pre-Auth' },
  { value: 'emi', label: 'EMI' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
];

export function LeadDetailDrawer({ lead, onClose }: LeadDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState<'info' | 'remarks' | 'docs' | 'calls'>('info');
  const [newRemark, setNewRemark] = useState('');
  const [remarkType, setRemarkType] = useState('general');

  const { data: detail, isLoading } = useLeadDetail(lead?.id ?? '');
  const addRemark = useAddRemark();
  const updateLead = useUpdateLead();

  const handleAddRemark = () => {
    if (!lead || !newRemark.trim()) return;
    addRemark.mutate(
      { leadId: lead.id, remark: newRemark.trim(), type: remarkType },
      { onSuccess: () => setNewRemark('') },
    );
  };

  const handleStatusChange = (status: LeadStatus) => {
    if (!lead) return;
    updateLead.mutate({ id: lead.id, status });
  };

  const tabs = [
    { id: 'info', label: 'Info', icon: <User size={14} /> },
    { id: 'remarks', label: `Remarks (${detail?.remarks?.length ?? 0})`, icon: <Activity size={14} /> },
    { id: 'docs', label: 'Documents', icon: <FileText size={14} /> },
    { id: 'calls', label: 'Calls', icon: <Phone size={14} /> },
  ];

  return (
    <Drawer isOpen={!!lead} onClose={onClose} title="Lead Details" size="lg">
      {isLoading ? (
        <div className="flex items-center justify-center py-20"><Spinner /></div>
      ) : detail ? (
        <div className="flex flex-col h-full">
          {/* Lead Header */}
          <div className="p-5 bg-slate-50 border-b border-slate-100">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{detail.name}</h2>
                <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                  <span className="font-mono">{formatPhone(detail.phone)}</span>
                  {detail.ptId && <span className="bg-slate-200 px-2 py-0.5 rounded text-xs font-mono">{detail.ptId}</span>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <StatusBadge status={detail.status} />
                <div className="flex gap-1">
                  <a href={`tel:${detail.phone}`} className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors">
                    <Phone size={14} />
                  </a>
                  <a
                    href={`https://wa.me/${detail.phone?.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100"
                  >
                    <MessageCircle size={14} />
                  </a>
                </div>
              </div>
            </div>

            {/* Quick status change */}
            <div className="mt-3">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Update Status</label>
              <select
                value={detail.status}
                onChange={e => handleStatusChange(e.target.value as LeadStatus)}
                className="w-full h-8 px-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-cyan/30"
              >
                {STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* Special flags */}
            <div className="flex gap-2 mt-3 flex-wrap">
              {detail.isInsuranceCase && (
                <button
                  onClick={() => updateLead.mutate({ id: detail.id, isInsuranceCase: false })}
                  className="px-2.5 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium"
                >
                  Insurance Case ×
                </button>
              )}
              {detail.isPreauthPending && (
                <button className="px-2.5 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                  Pre-Auth Pending
                </button>
              )}
              {detail.isEmiCase && (
                <button className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  EMI Case
                </button>
              )}
              {/* Quick assign buttons */}
              <button
                onClick={() => updateLead.mutate({ id: detail.id, isInsuranceCase: true, status: 'insurance' })}
                className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium hover:bg-orange-100 hover:text-orange-700 transition-colors"
              >
                + Insurance
              </button>
              <button
                onClick={() => updateLead.mutate({ id: detail.id, isEmiCase: true, status: 'emi' })}
                className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium hover:bg-blue-100 hover:text-blue-700 transition-colors"
              >
                + EMI
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-100 bg-white">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={clsx(
                  'flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors border-b-2',
                  activeTab === tab.id
                    ? 'border-brand-cyan text-brand-cyan'
                    : 'border-transparent text-slate-500 hover:text-slate-700',
                )}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-5">
            {activeTab === 'info' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Age', value: detail.age },
                    { label: 'Disease', value: detail.disease },
                    { label: 'Treatment', value: detail.treatment },
                    { label: 'Location', value: detail.location },
                    { label: 'City', value: detail.city },
                    { label: 'Category', value: detail.category },
                    { label: 'Email', value: detail.email },
                    { label: 'Due Date', value: formatDate(detail.dueDate) },
                    { label: 'OPD Date', value: formatDate(detail.opdDate) },
                    { label: 'IPD Date', value: formatDate(detail.ipdDate) },
                    { label: 'Assigned To', value: detail.assignedTo?.name },
                    { label: 'Follow-ups', value: String(detail.followupsCount) },
                    { label: 'Created', value: formatDateTime(detail.createdAt) },
                    { label: 'Updated', value: formatDateTime(detail.updatedAt) },
                  ].map(item => (
                    <div key={item.label} className="bg-slate-50 rounded-lg p-3">
                      <div className="text-xs font-medium text-slate-400 mb-0.5">{item.label}</div>
                      <div className="text-sm text-slate-900 font-medium">{item.value ?? '—'}</div>
                    </div>
                  ))}
                </div>
                {detail.source && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">Source:</span>
                    <SourceBadge source={detail.source} />
                  </div>
                )}
                {detail.tags?.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Tag size={14} className="text-slate-400" />
                    {detail.tags.map(tag => (
                      <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'remarks' && (
              <div className="space-y-4">
                {/* Add Remark */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <div className="flex gap-2">
                    <select
                      value={remarkType}
                      onChange={e => setRemarkType(e.target.value)}
                      className="h-8 px-2 text-xs rounded-lg border border-slate-200 bg-white"
                    >
                      <option value="general">General</option>
                      <option value="call">Call Note</option>
                      <option value="followup">Follow-up</option>
                      <option value="status_change">Status Change</option>
                    </select>
                  </div>
                  <textarea
                    value={newRemark}
                    onChange={e => setNewRemark(e.target.value)}
                    placeholder="Add a remark or note..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan/30 resize-none"
                  />
                  <Button
                    size="sm"
                    onClick={handleAddRemark}
                    loading={addRemark.isPending}
                    disabled={!newRemark.trim()}
                  >
                    Add Remark
                  </Button>
                </div>

                {/* Remarks Timeline */}
                <div className="space-y-3">
                  {detail.remarks?.map(remark => (
                    <div key={remark.id} className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-brand-navy/10 text-brand-navy flex items-center justify-center text-xs font-bold shrink-0">
                        {remark.user?.name?.[0] ?? '?'}
                      </div>
                      <div className="flex-1 bg-white rounded-xl border border-slate-100 p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-slate-700">{remark.user?.name ?? 'System'}</span>
                          <span className="text-xs text-slate-400">{formatDateTime(remark.createdAt)}</span>
                        </div>
                        <p className="text-sm text-slate-600">{remark.remark}</p>
                        {remark.type !== 'general' && (
                          <span className="mt-1.5 inline-block px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs">
                            {remark.type}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {!detail.remarks?.length && (
                    <div className="text-center py-8 text-slate-400 text-sm">No remarks yet</div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'docs' && (
              <div className="text-center py-8 text-slate-400 text-sm">
                Document management available in Documents section
              </div>
            )}

            {activeTab === 'calls' && (
              <div className="text-center py-8 text-slate-400 text-sm">
                Call history available in Calls section
              </div>
            )}
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}
